import React, { useState } from 'react';
import './DesignSystemPreview.css';

/**
 * Page de preview du Design System
 * Permet de visualiser tous les composants avant de les appliquer
 */
const DesignSystemPreview = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [inputValue, setInputValue] = useState('');

  return (
    <div className="design-preview">
      <div className="design-preview-container">
        {/* Header */}
        <div className="preview-header">
          <h1>🎨 Design System - Shop</h1>
          <p className="preview-subtitle">
            Prévisualisation des composants basés sur le nouveau design
          </p>
        </div>

        {/* Couleurs */}
        <section className="preview-section">
          <h2>🎨 Palette de couleurs</h2>

          <h3>Couleurs primaires</h3>
          <div className="color-palette">
            <div className="color-swatch" style={{ background: 'var(--color-primary-start)' }}>
              <span>#667eea</span>
              <small>Primary Start</small>
            </div>
            <div className="color-swatch" style={{ background: 'var(--color-primary-end)' }}>
              <span>#764ba2</span>
              <small>Primary End</small>
            </div>
            <div className="color-swatch gradient" style={{ background: 'var(--gradient-primary)' }}>
              <span>Gradient</span>
              <small>Primary</small>
            </div>
          </div>

          <h3>Couleurs de texte</h3>
          <div className="color-palette">
            <div className="color-swatch dark" style={{ background: 'var(--color-text-primary)' }}>
              <span>#1a202c</span>
              <small>Primary</small>
            </div>
            <div className="color-swatch dark" style={{ background: 'var(--color-text-secondary)' }}>
              <span>#2d3748</span>
              <small>Secondary</small>
            </div>
            <div className="color-swatch dark" style={{ background: 'var(--color-text-tertiary)' }}>
              <span>#4a5568</span>
              <small>Tertiary</small>
            </div>
            <div className="color-swatch" style={{ background: 'var(--color-text-muted)' }}>
              <span>#718096</span>
              <small>Muted</small>
            </div>
            <div className="color-swatch" style={{ background: 'var(--color-text-light)' }}>
              <span>#a0aec0</span>
              <small>Light</small>
            </div>
          </div>

          <h3>Couleurs sémantiques</h3>
          <div className="color-palette">
            <div className="color-swatch" style={{ background: 'var(--color-success)' }}>
              <span>✓</span>
              <small>Success</small>
            </div>
            <div className="color-swatch" style={{ background: 'var(--color-error)' }}>
              <span>✕</span>
              <small>Error</small>
            </div>
            <div className="color-swatch" style={{ background: 'var(--color-warning)' }}>
              <span>⚠</span>
              <small>Warning</small>
            </div>
            <div className="color-swatch" style={{ background: 'var(--color-info)' }}>
              <span>ℹ</span>
              <small>Info</small>
            </div>
          </div>
        </section>

        {/* Typographie */}
        <section className="preview-section">
          <h2>📝 Typographie</h2>
          <div className="typography-showcase">
            <div className="type-item">
              <span className="type-label">4xl (40px)</span>
              <h1 style={{ fontSize: 'var(--font-size-4xl)', margin: 0 }}>Titre principal</h1>
            </div>
            <div className="type-item">
              <span className="type-label">3xl (32px)</span>
              <h2 style={{ fontSize: 'var(--font-size-3xl)', margin: 0 }}>Titre secondaire</h2>
            </div>
            <div className="type-item">
              <span className="type-label">2xl (24px)</span>
              <h3 style={{ fontSize: 'var(--font-size-2xl)', margin: 0 }}>Sous-titre</h3>
            </div>
            <div className="type-item">
              <span className="type-label">xl (20px)</span>
              <h4 style={{ fontSize: 'var(--font-size-xl)', margin: 0 }}>Section title</h4>
            </div>
            <div className="type-item">
              <span className="type-label">lg (18px)</span>
              <p style={{ fontSize: 'var(--font-size-lg)', margin: 0 }}>Texte large / Lead</p>
            </div>
            <div className="type-item">
              <span className="type-label">base (16px)</span>
              <p style={{ fontSize: 'var(--font-size-base)', margin: 0 }}>Texte par défaut</p>
            </div>
            <div className="type-item">
              <span className="type-label">sm (14px)</span>
              <p style={{ fontSize: 'var(--font-size-sm)', margin: 0 }}>Petit texte</p>
            </div>
            <div className="type-item">
              <span className="type-label">xs (12px)</span>
              <p style={{ fontSize: 'var(--font-size-xs)', margin: 0 }}>Très petit texte</p>
            </div>
          </div>
        </section>

        {/* Boutons */}
        <section className="preview-section">
          <h2>🔘 Boutons</h2>

          <h3>Variantes</h3>
          <div className="button-showcase">
            <button className="btn btn-primary">
              <span>🚀</span> Primary
            </button>
            <button className="btn btn-secondary">Secondary</button>
            <button className="btn btn-outline">Outline</button>
            <button className="btn btn-ghost">Ghost</button>
            <button className="btn btn-danger">Danger</button>
          </div>

          <h3>Tailles</h3>
          <div className="button-showcase">
            <button className="btn btn-primary btn-sm">Small</button>
            <button className="btn btn-primary">Default</button>
            <button className="btn btn-primary btn-lg">Large</button>
          </div>

          <h3>États</h3>
          <div className="button-showcase">
            <button className="btn btn-primary" disabled>Disabled</button>
            <button className="btn btn-primary">
              <span className="spinner"></span>
              Loading...
            </button>
          </div>
        </section>

        {/* Formulaires */}
        <section className="preview-section">
          <h2>📋 Formulaires</h2>

          <div className="form-showcase">
            <div className="form-group">
              <label className="form-label">
                Nom de la boutique
                <span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="Ex: Ma Super Boutique"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              <p className="form-hint">Ce nom sera visible par vos clients.</p>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-input"
                rows="3"
                placeholder="Décrivez votre boutique..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Input avec erreur</label>
              <input
                type="text"
                className="form-input has-error"
                placeholder="Valeur incorrecte"
                defaultValue="Erreur"
              />
              <p className="form-error">Ce champ contient une erreur.</p>
            </div>

            <div className="form-group">
              <label className="checkbox-wrapper">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={checkboxChecked}
                  onChange={(e) => setCheckboxChecked(e.target.checked)}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label-text">
                  J'accepte les <a href="#cgu" className="link">conditions générales</a>
                </span>
              </label>
            </div>
          </div>
        </section>

        {/* Cards */}
        <section className="preview-section">
          <h2>🃏 Cards</h2>

          <div className="cards-showcase">
            <div className="card card-sm">
              <div className="card-header">
                <h3 style={{ margin: 0 }}>Card Header</h3>
              </div>
              <div className="card-body">
                <p>Contenu de la card avec le nouveau design system.</p>
              </div>
              <div className="card-footer">
                <button className="btn btn-secondary btn-sm">Annuler</button>
                <button className="btn btn-primary btn-sm">Confirmer</button>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <span style={{ fontSize: '3rem' }}>📦</span>
                <h3>Produit Premium</h3>
                <p style={{ color: 'var(--color-text-muted)' }}>
                  Description du produit avec des détails importants.
                </p>
                <p style={{ fontWeight: 'bold', fontSize: 'var(--font-size-xl)' }}>
                  29,99 €
                </p>
                <button className="btn btn-primary btn-block">Ajouter au panier</button>
              </div>
            </div>
          </div>
        </section>

        {/* Alertes */}
        <section className="preview-section">
          <h2>⚠️ Alertes</h2>

          <div className="alert alert-success">
            <span className="alert-icon">✓</span>
            Opération réussie ! Votre boutique a été créée.
          </div>

          <div className="alert alert-error">
            <span className="alert-icon">✕</span>
            Une erreur est survenue. Veuillez réessayer.
          </div>

          <div className="alert alert-warning">
            <span className="alert-icon">⚠</span>
            Attention : cette action est irréversible.
          </div>

          <div className="alert alert-info">
            <span className="alert-icon">ℹ</span>
            Astuce : vous pouvez personnaliser votre boutique à tout moment.
          </div>
        </section>

        {/* Badges */}
        <section className="preview-section">
          <h2>🏷️ Badges</h2>

          <div className="badge-showcase">
            <span className="badge badge-primary">Nouveau</span>
            <span className="badge badge-success">Actif</span>
            <span className="badge badge-error">En rupture</span>
            <span className="badge badge-warning">En attente</span>
          </div>
        </section>

        {/* Modal */}
        <section className="preview-section">
          <h2>💬 Modal</h2>

          <button className="btn btn-primary" onClick={() => setModalOpen(true)}>
            Ouvrir la modal
          </button>

          {modalOpen && (
            <div className="modal-overlay" onClick={() => setModalOpen(false)}>
              <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2 className="modal-title">Titre de la modal</h2>
                  <button className="modal-close" onClick={() => setModalOpen(false)}>
                    ✕
                  </button>
                </div>
                <div className="modal-body">
                  <p>
                    Ceci est le contenu de la modal avec le nouveau design system.
                    Elle utilise les variables CSS définies et suit la charte graphique.
                  </p>
                  <div className="form-group" style={{ marginTop: 'var(--spacing-6)' }}>
                    <label className="form-label">Un champ dans la modal</label>
                    <input type="text" className="form-input" placeholder="Saisissez quelque chose..." />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>
                    Annuler
                  </button>
                  <button className="btn btn-primary" onClick={() => setModalOpen(false)}>
                    Confirmer
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Spinners */}
        <section className="preview-section">
          <h2>⏳ Spinners</h2>

          <div className="spinner-showcase">
            <div>
              <div className="spinner"></div>
              <small>Default</small>
            </div>
            <div>
              <div className="spinner spinner-lg"></div>
              <small>Large</small>
            </div>
            <div style={{ background: 'var(--gradient-primary)', padding: '1rem', borderRadius: 'var(--radius-lg)' }}>
              <div className="spinner spinner-light"></div>
              <small style={{ color: 'white' }}>On gradient</small>
            </div>
          </div>
        </section>

        {/* Border Radius */}
        <section className="preview-section">
          <h2>📐 Border Radius</h2>

          <div className="radius-showcase">
            <div style={{ borderRadius: 'var(--radius-sm)' }}>
              <span>sm (4px)</span>
            </div>
            <div style={{ borderRadius: 'var(--radius-md)' }}>
              <span>md (6px)</span>
            </div>
            <div style={{ borderRadius: 'var(--radius-lg)' }}>
              <span>lg (8px)</span>
            </div>
          </div>
        </section>

        {/* Shadows */}
        <section className="preview-section">
          <h2>🌫️ Ombres</h2>

          <div className="shadow-showcase">
            <div style={{ boxShadow: 'var(--shadow-sm)' }}>
              <span>shadow-sm</span>
            </div>
            <div style={{ boxShadow: 'var(--shadow-md)' }}>
              <span>shadow-md</span>
            </div>
            <div style={{ boxShadow: 'var(--shadow-lg)' }}>
              <span>shadow-lg</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DesignSystemPreview;
